import React, { useContext, useState } from "react";
import { partition } from "lodash";
import { OmniboxSearchResult } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import { EmptyState } from "../Common/EmptyState";
import SmallLoading from "../Common/Icons/SmallLoading";
import { Tag } from "../Common/Tag";
import { Body } from "../Common/Typography";
import { SelectionCard } from "../Inputs/SelectionCard";
import { SearchResultsContext } from "../Maps/SearchResultsContext";
import { displayForResult } from "./utils";

const OmniboxResultItem = ({ result }: { result: OmniboxSearchResult }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { title, description, iconName, type } = displayForResult({ result });

  return (
    <SelectionCard.Item
      key={result.id}
      size="compact"
      borderStyle="ghost"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      value={result}
    >
      <FlexRow style={{ gap: spacing.m.value }}>
        <SelectionCard.CommonContent
          iconName={iconName}
          title={title}
          description={description}
        />
        <div style={{ opacity: isHovered ? 1 : 0 }}>
          <Tag styleVariant="default" size="in-line">
            {type}
          </Tag>
        </div>
      </FlexRow>
    </SelectionCard.Item>
  );
};

const OmniboxResultsLoading = ({ expand }: { expand: boolean }) => {
  return expand ?
      <FlexColumn
        style={{
          gap: spacing.m.value,
          paddingTop: spacing["3xl"].value,
          alignItems: "center",
        }}
      >
        <SmallLoading />
        <Body type="emphasis" size="default" color="contentPlaceholder">
          Loading...
        </Body>
      </FlexColumn>
    : <FlexColumn style={{ alignItems: "center", padding: `${spacing.xs} 0` }}>
        <SmallLoading />
      </FlexColumn>;
};

export const OmniboxResults = ({
  handleSearchResultClick,
  expandPlaceholders,
}: {
  handleSearchResultClick: (searchResult: OmniboxSearchResult) => void;
  expandPlaceholders: boolean;
}) => {
  const { searchResultsState } = useContext(SearchResultsContext);

  if (!searchResultsState.debouncedQuery || searchResultsState.loading) {
    return <OmniboxResultsLoading expand={expandPlaceholders} />;
  } else if (!searchResultsState.results?.length) {
    return expandPlaceholders ?
        <EmptyState
          icon="missing-location"
          paddingTop={spacing["2xl"].value}
          title="No results found"
          body="Check the spelling or search by street, city, or ZIP, then use the map to explore nearby."
        />
      : <EmptyState
          compact
          title="No results found"
          paddingTop={0}
          paddingBottom={0}
        />;
  }

  const [strictMatches, fuzzyMatches] = partition(
    searchResultsState.results,
    result => result.strictMatch,
  );

  return (
    <SelectionCard.Group
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      {strictMatches.length > 0 && (
        <Body
          type="emphasis"
          size="small"
          style={{ padding: `${spacing.s} ${spacing.m} 0` }}
        >
          {searchResultsState.isTextSearch ? "Exact Matches" : "Nearby Results"}
        </Body>
      )}
      {strictMatches.map(result => (
        <OmniboxResultItem result={result} key={result.id} />
      ))}
      {fuzzyMatches.length > 0 && (
        <>
          <Body
            type="emphasis"
            size="small"
            style={{
              paddingTop: strictMatches.length > 0 ? spacing.m.value : 0,
              paddingLeft: spacing.m.value,
              paddingRight: spacing.m.value,
            }}
          >
            Related Results
          </Body>
          {fuzzyMatches.map(result => (
            <OmniboxResultItem result={result} key={result.id} />
          ))}
        </>
      )}
    </SelectionCard.Group>
  );
};
